// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, { CartPageTemplateStyles } from "@eisinc/App/Components/Cart/CartPageTemplate";
import XngZone from "@eisinc/App/Components/Common/XngZone";
import { StepperContext } from "@eisinc/App/Widgets/CheckoutStepper/StepperContext";
import { XNGAGE_ELEMENTS } from "@eisinc/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

enum fields {
    modifyPageTitle = "modifyPageTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.modifyPageTitle]: boolean;
    };
}

type Props = OwnProps;

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {
    headerTitle?: TypographyPresentationProps;
    productListViewExpandLink?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    titleText?: TypographyProps;
    contentWrapper?: InjectableCss;
}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {
    headerTitle: {
        variant: "h1",
        css: css`
            margin-bottom: 0;
        `,
    },
};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({ id, fields: { modifyPageTitle } }) => {
    const { currentStep } = useContext(StepperContext);
    const { isEditing } = useShellContext();

    const isPickUp = useAppSelector(state => state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp);
    const checkoutTitles = [
        "Cart",
        "Checkout - Addresses",
        isPickUp ? "Checkout - PickUp" : "Checkout - Shipping",
        "Checkout - Payment",
        "Checkout - Review",
    ];

    return (
        <CartPageTemplate extendedStyles={styles}>
            <CartPageTemplate.HeaderSection>
                <CartPageTemplate.TitleSection>
                    {modifyPageTitle ? (
                        <XngZone contentId={id} zoneName="Content7" hint="Zone For Page Title" />
                    ) : (
                        <Typography {...styles.headerTitle}>{translate(checkoutTitles[currentStep])}</Typography>
                    )}
                </CartPageTemplate.TitleSection>
                <XngZone contentId={id} zoneName="Content6" hint="Zone For Stepper" />
                <CartPageTemplate.CartApproverMessage>
                    <XngZone contentId={id} zoneName="Content0" hint="Zone For Header (Cart Approval message)" />
                </CartPageTemplate.CartApproverMessage>
                {currentStep !== 3 && currentStep !== 4 && (
                    <XngZone contentId={id} zoneName="Content8" hint="Zone For Rich Content" />
                )}
            </CartPageTemplate.HeaderSection>
            <CartPageTemplate.MainSection>
                <XngZone
                    contentId={id}
                    zoneName="Content1"
                    hint="Zone For Step Content (Shipping container, Submit & Review container)"
                />
                <AddToListModal />
            </CartPageTemplate.MainSection>
            <CartPageTemplate.CartTotalSection>
                {(currentStep === 3 || currentStep === 4 || isEditing) && (
                    <XngZone contentId={id} zoneName="Content4" hint="Zone For Promo Code" />
                )}
                <XngZone contentId={id} zoneName="Content5" hint="Zone For Request Certifications Warning Message" />
                <XngZone contentId={id} zoneName="Content2" hint="Zone For Checkout Total" />
                <XngZone contentId={id} zoneName="Content3" hint="Zone For Authored Content" />
            </CartPageTemplate.CartTotalSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutPageContainer,
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage", "OrderConfirmationPage"],
        displayName: "Checkout Page Container",
        fieldDefinitions: [
            {
                name: fields.modifyPageTitle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
